import {
  createWalletClient,
  createPublicClient,
  http,
  parseGwei,
  type Account,
  type Chain,
  type Hex,
} from "viem";
import {
  mainnet,
  base,
  arbitrum,
  optimism,
  polygon,
} from "viem/chains";
import { mnemonicToAccount } from "viem/accounts";
import type { EvmChainId } from "./chains";

const CHAIN_BY_ID: Record<EvmChainId, Chain> = {
  ETH: mainnet,
  BASE: base,
  ARB: arbitrum,
  OP: optimism,
  POLYGON: polygon,
};

const chainForLeokitChainId = (leokitChainId: number): Chain => {
  switch (leokitChainId) {
    case 1:
      return mainnet;
    case 8453:
      return base;
    case 42161:
      return arbitrum;
    case 10:
      return optimism;
    case 137:
      return polygon;
    default:
      throw new Error(`Unsupported EVM chainId ${leokitChainId}`);
  }
};

export interface UnsignedEvmTx {
  to: `0x${string}`;
  data?: `0x${string}`;
  value?: string;
  gas?: string;
  chainId: number;
}

/**
 * Sign and broadcast a LeoKit-supplied EVM swap transaction. Uses
 * viem's `walletClient.sendTransaction` against the chain's default
 * public RPC; gas + nonce are filled in by the client when omitted.
 *
 * Returns the broadcast tx hash. Throws if the chain isn't one we know,
 * if RPC submission fails, or if the user's address has no balance for
 * the principal + gas.
 */
export async function signAndSendEvmTx(args: {
  phrase: string;
  /** EVM chain ID matching the unsigned tx's chainId field. */
  chainId: EvmChainId;
  unsigned: UnsignedEvmTx;
}): Promise<{ hash: Hex; from: `0x${string}` }> {
  // Hard-fail if LeoKit's unsigned tx targets a chain that doesn't
  // match the source asset's chain. Without this guard, a backend bug
  // or hostile response could route a Polygon-USDC swap onto mainnet
  // ETH from the user's account, draining gas to a meaningless `to`.
  const expectedChain = CHAIN_BY_ID[args.chainId];
  if (!expectedChain) {
    throw new Error(`Unsupported chain for sending: ${args.chainId}`);
  }
  if (
    args.unsigned.chainId !== 0 &&
    args.unsigned.chainId !== expectedChain.id
  ) {
    throw new Error(
      `Chain mismatch: source asset is ${args.chainId} (chainId ${expectedChain.id}) but LeoKit returned an unsigned tx for chainId ${args.unsigned.chainId}. Refusing to broadcast.`,
    );
  }
  const chain = expectedChain;
  const account: Account = mnemonicToAccount(args.phrase, { accountIndex: 0 });
  const walletClient = createWalletClient({
    account,
    chain,
    transport: http(),
  });
  const publicClient = createPublicClient({ chain, transport: http() });

  // viem expects bigint for value/gas; LeoKit returns strings.
  const value =
    args.unsigned.value && args.unsigned.value !== "0"
      ? BigInt(args.unsigned.value)
      : 0n;
  // Re-estimate gas locally with a 15% headroom. An estimation failure
  // is usually the simulator saying "this tx WILL revert" — most often
  // because an ERC-20 approval is missing or a deadline expired. We
  // surface that to the user instead of broadcasting a doomed tx that
  // would burn the user's gas. Only fall back to LeoKit's hint when
  // it's explicitly large enough to suggest the backend did its own
  // simulation upstream.
  let gas: bigint | undefined;
  try {
    gas = await publicClient.estimateGas({
      account,
      to: args.unsigned.to,
      data: args.unsigned.data,
      value,
    });
    gas = (gas * 115n) / 100n;
  } catch (e) {
    const fallback = args.unsigned.gas
      ? BigInt(args.unsigned.gas)
      : undefined;
    // 100k gas is the rough lower bound for any non-trivial swap call;
    // a sensible upstream estimate clears that easily. If the fallback
    // is missing or suspiciously low we'd just OOG on broadcast.
    if (fallback && fallback >= 100_000n) {
      gas = fallback;
    } else {
      const msg = e instanceof Error ? e.message : String(e);
      throw new Error(
        `Gas estimation failed and no usable upstream estimate. The transaction would likely revert (often: missing ERC-20 approval, expired quote, or insufficient balance). Original error: ${msg}`,
      );
    }
  }

  // viem picks a sensible maxFeePerGas from the chain by default; we
  // bump tip slightly to land in the next block on busy chains.
  const hash = await walletClient.sendTransaction({
    to: args.unsigned.to,
    data: args.unsigned.data,
    value,
    gas,
    maxPriorityFeePerGas: parseGwei("1.5"),
  });
  return { hash, from: account.address };
}
